import type { ReactNode } from "react";
import type { BlockBackground } from "@/types";
import { backgroundStyle, isDarkBackground } from "./background";

/**
 * The one cross-cutting piece (§5A + CD port). Every block renders as a
 * full-bleed **band**: the wrapper spans edge-to-edge and carries the optional
 * background colour/image; an inner 1120px container centres the content. Text
 * flips to the on-dark token over a dark colour or image scrim — centralised
 * here via a data attribute the renderer CSS keys off, so blocks never
 * reimplement it. The cover block gets the `--hero` modifier (86vh, title at
 * the bottom).
 */
export function BackgroundWrapper({
  background,
  blockType,
  sectionKey,
  children,
}: {
  background?: BlockBackground;
  /** Block type as a plain string (AnyBlock includes the forward-compatible
   *  UnknownBlock); only the "cover" hero is special-cased here. */
  blockType?: string;
  /** Engagement-beacon hook: stamped as data-section-key so the shared beacon can attribute
   *  dwell to this band. Purely an attribute — no layout, styling or behaviour hangs off it. */
  sectionKey?: string;
  children: ReactNode;
}) {
  const onDark = isDarkBackground(background);
  const isHero = blockType === "cover";

  return (
    <div
      className={`block-bg${isHero ? " block-bg--hero" : ""}`}
      data-on-dark={onDark ? "true" : undefined}
      data-section-key={sectionKey}
      style={backgroundStyle(background)}
    >
      <div className="qr-inner">{children}</div>
    </div>
  );
}
